<template>
  <div class="landing-page light-saas-theme">
    <!-- Navbar -->
    <header class="saas-header d-none d-md-block">
      <div class="container d-flex justify-content-between align-items-center">
        <div class="brand-logo">
          <div class="logo-mark"><AppIcon name="bi-layers-half" class="bi bi-layers-half" /></div>
          <span class="logo-text">Gstocky</span>
        </div>
        <div class="header-actions">
          <router-link to="/login" class="btn-login-outline">{{ $t('login') }}</router-link>
        </div>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="hero-section-saas">
      <div class="hero-bg-shapes">
        <div class="shape shape-primary"></div>
        <div class="shape shape-secondary"></div>
      </div>
      
      <div class="container hero-container position-relative z-1">
        <div class="row align-items-center min-vh-80 py-5">
          <!-- Mobile Top: 30 days free -->
          <div class="col-12 d-block d-md-none text-center mb-4 order-1">
            <div class="pill-badge">
              <span class="pill-icon">✨</span>
              <span class="pill-text fw-semibold">{{ $t('lp_30_days_free') }}</span>
            </div>
          </div>

          <!-- Left Content -->
          <div class="col-lg-6 hero-text-col pe-lg-5 order-3 order-md-1 mt-5 mt-md-0 mb-5 mb-lg-0">
            <div class="pill-badge mb-4 d-none d-md-inline-flex">
              <span class="pill-icon">✨</span>
              <span class="pill-text fw-semibold">{{ $t('lp_30_days_free') }}</span>
            </div>
            
            <h1 class="hero-title-saas mb-4">
              {{ $t('lp_hero_title') }}
            </h1>
            
            <p class="hero-subtitle-saas mb-5">
              {{ $t('lp_hero_subtitle') }}
            </p>

            <ul class="hero-checklist list-unstyled mb-0">
              <li><AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill text-primary-saas" /> {{ $t('lp_secure_data') }}</li>
              <li><AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill text-primary-saas" /> {{ $t('lp_setup_minutes') }}</li>
              <li><AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill text-primary-saas" /> {{ $t('lp_no_credit_card') }}</li>
            </ul>
          </div>
          
          <!-- Right Content: Form -->
          <div class="col-lg-6 position-relative form-col order-2">
            <div class="dots-pattern"></div>
            
            <div class="saas-form-card">
              <div v-if="sent" class="success-state text-center py-5">
                <div class="success-icon-wrapper mx-auto mb-4">
                  <AppIcon name="bi-check-lg" class="bi bi-check-lg" />
                </div>
                <h3 class="fw-bold text-dark mb-2">{{ $t('check_your_email') }}</h3>
                <p class="text-muted">{{ $t("confirmation_link_sent_to_your_Email") }}</p>
              </div>
              
              <div v-else>
                <div class="form-header-saas mb-4">
                  <h3 class="fw-bold text-dark">{{ $t('lp_create_account_btn') }}</h3>
                  <p class="text-muted small m-0">{{ $t('lp_password_hint') }}</p>
                </div>
                
                <form @submit.prevent="handleSignup" class="saas-form">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="saas-label">{{ $t('name') }} <span class="text-danger">*</span></label>
                      <input type="text" v-model="form.name" :placeholder="$t('name')" class="saas-input" required />
                    </div>
                    
                    <div class="col-md-6">
                      <label class="saas-label">{{ $t('phone') }} <span class="text-danger">*</span></label>
                      <input type="tel" v-model="form.phone" :placeholder="$t('phone_placeholder')" class="saas-input" required />
                    </div>
                    
                    <div class="col-12">
                      <label class="saas-label">{{ $t('email') }} <span class="text-danger">*</span></label>
                      <input type="email" v-model="form.email" :placeholder="$t('email')" class="saas-input" required />
                    </div>
                    
                    <div class="col-12 mb-2">
                      <label class="saas-label">{{ $t('password') }} <span class="text-danger">*</span></label>
                      <input type="password" v-model="form.password" :placeholder="$t('password')" class="saas-input" required minlength="6" />
                    </div>
                  </div>
                  
                  <button type="submit" class="btn-saas-primary w-100 mt-4" :disabled="loading" :class="{'is-loading': loading}">
                    <span v-if="!loading">{{ $t('lp_create_account_btn') }}</span>
                    <span v-else><AppIcon name="bi-arrow-repeat" class="bi bi-arrow-repeat spin" /> {{ $t('loading') }}</span>
                  </button>
                  
                  <div class="form-footer-saas mt-4 text-center">
                    <p class="text-muted m-0 small">
                      {{ $t('lp_already_have_account') }} 
                      <router-link to="/login" class="login-link-saas fw-semibold">{{ $t('login') }}</router-link>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-saas py-6 bg-white">
      <div class="container py-5">
        <div class="text-center max-w-3xl mx-auto mb-5 pb-3">
          <p class="section-subtitle-saas text-uppercase fw-bold text-primary-saas letter-spacing-1 mb-2 d-none d-md-block">{{ $t('lp_features_title') }}</p>
          <h2 class="section-title-saas fw-bold text-dark">{{ $t('lp_features_subtitle') }}</h2>
        </div>
        
        <div class="row g-4 g-lg-5">
          <div class="col-lg-4 col-md-6" v-for="(feature, index) in featuresList" :key="index">
            <div class="feature-card-saas h-100">
              <div class="feature-icon-saas mb-4">
                <AppIcon :name="feature.icon" :class="feature.icon" />
              </div>
              <h4 class="feature-title-saas fw-bold text-dark mb-3">{{ feature.title }}</h4>
              <p class="feature-desc-saas text-muted m-0 lh-lg">{{ feature.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mobile App Section -->
    <section class="mobile-saas overflow-hidden position-relative py-6">
      <div class="mobile-bg-shape"></div>
      <div class="container position-relative z-1 py-5">
        <div class="row align-items-center">
          <div class="col-lg-5 mb-5 mb-lg-0">
            <div class="mobile-badge-saas mb-4 d-inline-block">
              <AppIcon name="bi-phone" class="bi bi-phone me-2" /> {{ $t('lp_mobile_friendly') }}
            </div>
            <h2 class="section-title-saas text-white fw-bold mb-4">{{ $t('lp_mobile_title') }}</h2>
            <p class="text-white-50 fs-5 mb-5 lh-lg">{{ $t('lp_mobile_description') }}</p>
            
            <div class="mobile-features-list">
              <div class="m-feature d-flex align-items-center mb-3">
                <div class="m-icon me-3"><AppIcon name="bi-check-lg" class="bi bi-check-lg text-white" /></div>
                <span class="text-white fw-medium fs-5">{{ $t('lp_mobile_feature_1') }}</span>
              </div>
              <div class="m-feature d-flex align-items-center mb-3">
                <div class="m-icon me-3"><AppIcon name="bi-check-lg" class="bi bi-check-lg text-white" /></div>
                <span class="text-white fw-medium fs-5">{{ $t('lp_mobile_feature_2') }}</span>
              </div>
              <div class="m-feature d-flex align-items-center">
                <div class="m-icon me-3"><AppIcon name="bi-check-lg" class="bi bi-check-lg text-white" /></div>
                <span class="text-white fw-medium fs-5">{{ $t('lp_mobile_feature_3') }}</span>
              </div>
            </div>
          </div>
          <div class="col-lg-7 d-flex justify-content-center justify-content-lg-end">
            <img src="/images/phone-frame.png" alt="Mobile App" class="mobile-mockup-saas shadow-lg rounded-4" />
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits & Stats Section -->
    <section class="benefits-saas py-6 bg-light-gray">
      <div class="container py-5">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-5 mb-lg-0 pe-lg-5">
            <h2 class="section-title-saas fw-bold text-dark mb-5">{{ $t('lp_benefits_title') }}</h2>
            
            <div class="benefit-item-saas d-flex mb-4 p-3 rounded-4 bg-white shadow-sm hover-lift">
              <div class="b-icon-saas me-4 flex-shrink-0"><AppIcon name="bi-lightning-charge" class="bi bi-lightning-charge" /></div>
              <div class="b-text-saas">
                <h5 class="fw-bold text-dark mb-2">{{ $t('lp_benefit_fast_title') }}</h5>
                <p class="text-muted m-0">{{ $t('lp_benefit_fast_desc') }}</p>
              </div>
            </div>
            
            <div class="benefit-item-saas d-flex mb-4 p-3 rounded-4 bg-white shadow-sm hover-lift">
              <div class="b-icon-saas me-4 flex-shrink-0"><AppIcon name="bi-shield-check" class="bi bi-shield-check" /></div>
              <div class="b-text-saas">
                <h5 class="fw-bold text-dark mb-2">{{ $t('lp_benefit_secure_title') }}</h5>
                <p class="text-muted m-0">{{ $t('lp_benefit_secure_desc') }}</p>
              </div>
            </div>
            
            <div class="benefit-item-saas d-flex p-3 rounded-4 bg-white shadow-sm hover-lift">
              <div class="b-icon-saas me-4 flex-shrink-0"><AppIcon name="bi-headset" class="bi bi-headset" /></div>
              <div class="b-text-saas">
                <h5 class="fw-bold text-dark mb-2">{{ $t('lp_benefit_support_title') }}</h5>
                <p class="text-muted m-0">{{ $t('lp_benefit_support_desc') }}</p>
              </div>
            </div>
          </div>
          
          <div class="col-lg-6 ps-lg-5">
            <div class="stats-grid-saas">
              <div class="stat-card-saas primary-stat text-center p-5 rounded-4 shadow-sm bg-white hover-lift">
                <div class="stat-icon mb-3"><AppIcon name="bi-people-fill" class="bi bi-people-fill text-primary-saas fs-1" /></div>
                <div class="stat-number fw-bold text-dark display-5 mb-2">10k+</div>
                <div class="stat-label text-muted fw-medium">{{ $t('lp_stat_users') }}</div>
              </div>
              
              <div class="stat-card-saas secondary-stat text-center p-5 rounded-4 shadow-sm bg-white hover-lift">
                <div class="stat-icon mb-3"><AppIcon name="bi-hdd-network-fill" class="bi bi-hdd-network-fill text-primary-saas fs-1" /></div>
                <div class="stat-number fw-bold text-dark display-5 mb-2">99.9%</div>
                <div class="stat-label text-muted fw-medium">{{ $t('lp_stat_uptime') }}</div>
              </div>
              
              <div class="stat-card-saas tertiary-stat text-center p-5 rounded-4 shadow-sm bg-white hover-lift">
                <div class="stat-icon mb-3"><AppIcon name="bi-clock-fill" class="bi bi-clock-fill text-primary-saas fs-1" /></div>
                <div class="stat-number fw-bold text-dark display-5 mb-2">24/7</div>
                <div class="stat-label text-muted fw-medium">{{ $t('lp_stat_support') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer-saas bg-white border-top py-4">
      <div class="container text-center">
        <p class="text-muted small m-0 fw-medium">&copy; 2026 Gstocky. {{ $t('lp_footer_rights') }}</p>
      </div>
    </footer>
  </div>
</template>

<script>
import helper from "../helper";

export default {
  name: 'LandingPage',
  mounted() {
    this.setLanguageFromQuery();
    window.mixpanel.track("landing_page_view");
    document.title = "Gstocky - Manage Your Business Efficiently";
    this.initCrisp();
  },
  beforeDestroy() {
    this.removeCrisp();
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        phone: '',
        password: ''
      },
      loading: false,
      sent: false
    }
  },
  computed: {
    featuresList() {
      return [
        { icon: 'bi bi-box-seam', title: this.$t('inventory_management'), desc: this.$t('lp_inventory_desc') },
        { icon: 'bi bi-people', title: this.$t('crm_customer_relations'), desc: this.$t('lp_crm_desc') },
        { icon: 'bi bi-receipt', title: this.$t('sales_invoicing'), desc: this.$t('lp_invoicing_desc') },
        { icon: 'bi bi-cart', title: this.$t('point_of_sale'), desc: this.$t('lp_pos_desc') },
        { icon: 'bi bi-credit-card', title: this.$t('payment_management'), desc: this.$t('lp_payment_desc') },
        { icon: 'bi bi-graph-up', title: this.$t('lp_analytics'), desc: this.$t('lp_analytics_desc') }
      ];
    }
  },
  methods: {
    async handleSignup() {
      if (!this.validateForm()) return;

      try {
        this.loading = true;
        await helper.customPost(
          `${process.env.VUE_APP_API}/register`,
          {
            email: this.form.email,
            password: this.form.password,
            name: this.form.name,
            phone: this.form.phone,
          }
        );
        this.loading = false;
        this.sent = true;
        this.showSuccess(this.$t('lp_signup_success'));
      } catch (error) {
        this.loading = false;
        this.showError(error.response?.data?.message || this.$t('lp_signup_error'));
      }
    },

    validateForm() {
      if (!this.form.name.trim() || !this.form.email.trim() || !this.form.phone.trim() || !this.form.password.trim()) {
        this.showError(this.$t("please_complete_all_required_fields"));
        return false;
      }

      if (!this.isValidEmail(this.form.email)) {
        this.showError("Invalid email address");
        return false;
      }

      if (this.form.password.length < 6) {
        this.showError("Password must be at least 6 characters");
        return false;
      }

      return true;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    showError(message) {
      window.vueOpener.open({ message: message, type: "error", position: "bottom" });
    },

    showSuccess(message) {
      window.vueOpener.open({ message: message, type: "success", position: "bottom" });
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    setLanguageFromQuery() {
      const urlParams = new URLSearchParams(window.location.search);
      const lang = urlParams.get('lang');
      const supportedLanguages = ['en', 'fr', 'ar'];
      
      let targetLang = 'en'; // default fallback

      if (lang && supportedLanguages.includes(lang)) {
        targetLang = lang;
        localStorage.setItem('landingPageLang', lang);
      } else {
        const savedLang = localStorage.getItem('landingPageLang');
        if (savedLang && supportedLanguages.includes(savedLang)) {
          targetLang = savedLang;
        } else {
          // Detect browser language
          const browserLang = navigator.language || navigator.userLanguage;
          const shortBrowserLang = browserLang ? browserLang.split('-')[0] : null;
          
          if (shortBrowserLang && supportedLanguages.includes(shortBrowserLang)) {
            targetLang = shortBrowserLang;
          }
        }
      }

      this.$i18n.locale = targetLang;
      if (targetLang === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', 'ar');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
        document.documentElement.setAttribute('lang', targetLang);
      }
    },

    initCrisp() {
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:show"]);
        return;
      }
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = "3aa4fac3-42c0-4b5d-9e93-19d7def099d1"; 
      const script = document.createElement("script");
      script.src = "https://client.crisp.chat/l.js";
      script.async = true;
      document.getElementsByTagName("head")[0].appendChild(script);
    },

    removeCrisp() {
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:hide"]);
      }
    }
  }
}
</script>

<style scoped>
/* Base SaaS Theme Colors */
:root {
  --primary: #4f46e5;
  --primary-hover: #4338ca;
  --primary-light: #e0e7ff;
  --text-main: #111827;
  --text-muted: #6b7280;
  --bg-main: #f8fafc;
  --bg-card: #ffffff;
  --border-color: #e5e7eb;
}

.light-saas-theme {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--text-main);
  background-color: var(--bg-main);
  overflow-x: hidden;
}

.text-primary-saas { color: #4f46e5 !important; }
.bg-light-gray { background-color: #f8fafc; }
.py-6 { padding-top: 6rem; padding-bottom: 6rem; }
.lh-lg { line-height: 1.7 !important; }
.letter-spacing-1 { letter-spacing: 1px; }
.hover-lift { transition: transform 0.3s ease, box-shadow 0.3s ease; }
.hover-lift:hover { transform: translateY(-5px); box-shadow: 0 15px 30px rgba(0,0,0,0.05) !important; }

/* Header */
.saas-header {
  position: absolute;
  top: 0; left: 0; width: 100%;
  padding: 24px 0;
  z-index: 50;
}
.brand-logo {
  display: flex; align-items: center; gap: 10px;
}
.logo-mark {
  width: 36px; height: 36px;
  background: #4f46e5;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 1.2rem;
}
.logo-text { font-size: 1.5rem; font-weight: 800; color: #111827; letter-spacing: -0.5px; }

.btn-login-outline {
  display: inline-block;
  padding: 8px 24px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-weight: 600;
  text-decoration: none;
  background: white;
  transition: all 0.2s ease;
}
.btn-login-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
  background: #f8fafc;
}

/* Hero Section */
.hero-section-saas {
  position: relative;
  background-color: #f8fafc;
  min-height: 100vh;
  padding-top: 80px;
  overflow: hidden;
}
.hero-bg-shapes {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden; z-index: 0; pointer-events: none;
}
.shape {
  position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.5;
}
.shape-primary {
  width: 600px; height: 600px; background: #e0e7ff; top: -100px; left: -100px;
}
.shape-secondary {
  width: 500px; height: 500px; background: #fae8ff; bottom: 0; right: -100px;
}

.pill-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: white;
  padding: 8px 16px;
  border-radius: 50px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  animation: fadeDown 0.6s ease-out forwards;
}
.pill-icon { font-size: 1rem; }
.pill-text { color: #4f46e5; font-size: 0.9rem; }

.hero-title-saas {
  font-size: 3.2rem;
  font-weight: 800;
  line-height: 1.2;
  color: #111827;
  letter-spacing: -1px;
  animation: fadeUp 0.8s ease-out 0.2s forwards; opacity: 0;
}

.hero-subtitle-saas {
  font-size: 1.25rem;
  color: #4b5563;
  line-height: 1.6;
  max-width: 90%;
  animation: fadeUp 0.8s ease-out 0.4s forwards; opacity: 0;
}

.hero-checklist {
  animation: fadeUp 0.8s ease-out 0.6s forwards; opacity: 0;
}
.hero-checklist li {
  font-size: 1.1rem; color: #374151; margin-bottom: 12px; display: flex; align-items: center; gap: 10px; font-weight: 500;
}

/* Hero Form */
.form-col {
  animation: fadeLeft 0.8s ease-out 0.8s forwards; opacity: 0;
}
.saas-form-card {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0,0,0,0.05);
  position: relative; z-index: 2;
}
.dots-pattern {
  position: absolute;
  width: 100px; height: 100px;
  background-image: radial-gradient(#cbd5e1 2px, transparent 2px);
  background-size: 16px 16px;
  bottom: -20px; right: -20px; z-index: 1;
}

.saas-label {
  display: block; font-weight: 600; font-size: 0.9rem; color: #374151; margin-bottom: 6px;
}
.saas-input {
  width: 100%;
  padding: 12px 16px;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  color: #111827;
  transition: all 0.2s ease;
}
.saas-input:focus {
  outline: none;
  border-color: #4f46e5;
  background: white;
  box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.1);
}
.saas-input::placeholder { color: #9ca3af; }

.btn-saas-primary {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.3);
}
.btn-saas-primary:hover {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 6px 8px -1px rgba(79, 70, 229, 0.4);
}
.btn-saas-primary.is-loading { opacity: 0.7; pointer-events: none; }

.login-link-saas { color: #4f46e5; text-decoration: none; }
.login-link-saas:hover { text-decoration: underline; color: #3730a3; }

.success-icon-wrapper {
  width: 64px; height: 64px;
  background: #d1fae5; color: #059669;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 2rem;
}

/* Features */
.feature-card-saas {
  padding: 20px; border-radius: 12px; transition: transform 0.2s ease;
}
.feature-icon-saas {
  width: 48px; height: 48px;
  background: #e0e7ff; color: #4f46e5;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem;
}

/* Mobile Section */
.mobile-saas { background: #1e293b; }
.mobile-bg-shape {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: radial-gradient(circle at top right, #334155 0%, transparent 50%);
  z-index: 0;
}
.mobile-badge-saas {
  background: rgba(255,255,255,0.1); padding: 8px 16px; border-radius: 50px; color: white; font-weight: 600; font-size: 0.9rem;
}
.m-icon {
  width: 28px; height: 28px; background: rgba(255,255,255,0.2); border-radius: 50%; display: flex; align-items: center; justify-content: center;
}
.mobile-mockup-saas { max-height: 600px; transform: rotate(-5deg); transition: transform 0.5s ease; }
.mobile-mockup-saas:hover { transform: rotate(0deg) translateY(-10px); }

/* Benefits */
.b-icon-saas {
  width: 48px; height: 48px;
  background: #f3f4f6; color: #4b5563;
  border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem;
  transition: all 0.3s ease;
}
.benefit-item-saas:hover .b-icon-saas { background: #4f46e5; color: white; }

.stats-grid-saas { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.stat-icon { width: 64px; height: 64px; background: #e0e7ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; }
.primary-stat { grid-column: 1 / 3; }
@media (min-width: 768px) {
  .primary-stat { grid-column: 1 / 2; }
}

/* Animations */
@keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes fadeDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes fadeLeft { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }

/* RTL Support */
[dir="rtl"] .light-saas-theme { text-align: right; }
[dir="rtl"] .brand-logo { flex-direction: row-reverse; }
[dir="rtl"] .hero-checklist li { flex-direction: row-reverse; }
[dir="rtl"] .hero-checklist { align-items: flex-end; text-align: right; }
[dir="rtl"] .pill-badge { flex-direction: row-reverse; }
[dir="rtl"] .m-feature { flex-direction: row-reverse; }
[dir="rtl"] .m-icon { margin-right: 0 !important; margin-left: 1rem; }
[dir="rtl"] .b-icon-saas { margin-right: 0 !important; margin-left: 1.5rem; }
[dir="rtl"] .pe-lg-5 { padding-right: 0 !important; padding-left: 3rem !important; }
[dir="rtl"] .ps-lg-5 { padding-left: 0 !important; padding-right: 3rem !important; }

/* Responsive Adjustments */
@media (max-width: 991px) {
  .hero-title-saas { font-size: 2.5rem; }
  .hero-text-col { text-align: center; margin-bottom: 40px; }
  .hero-subtitle-saas { margin: 0 auto 30px; }
  .hero-checklist { display: inline-flex; flex-direction: column; align-items: flex-start; text-align: left; }
  .mobile-mockup-saas { max-height: 450px; transform: rotate(0); margin-top: 40px; }
  [dir="rtl"] .hero-text-col { text-align: center; }
}
@media (max-width: 767px) {
  .saas-header { padding: 16px 0; }
  .logo-mark { width: 32px; height: 32px; font-size: 1rem; }
  .logo-text { font-size: 1.25rem; }
  .btn-login-outline { padding: 6px 16px; font-size: 0.9rem; }
  
  .hero-section-saas { padding-top: 90px; min-height: auto; padding-bottom: 40px; }
  .pill-badge { padding: 6px 12px; }
  .pill-text { font-size: 0.85rem; }
  .hero-title-saas { font-size: 1.75rem; }
  .hero-subtitle-saas { font-size: 1rem; }
  .hero-checklist li { font-size: 0.95rem; }
  
  .saas-form-card { padding: 20px; }
  .form-header-saas h3 { font-size: 1.25rem; }
  .saas-input { padding: 10px 14px; font-size: 0.95rem; }
  .btn-saas-primary { padding: 12px; font-size: 1rem; }
  
  .stats-grid-saas { grid-template-columns: 1fr; gap: 16px; }
  .primary-stat, .secondary-stat, .tertiary-stat { grid-column: 1 / -1; padding: 1.5rem !important; }
  .stat-icon { width: 48px; height: 48px; }
  .stat-icon i { font-size: 1.25rem !important; }
  .stat-number { font-size: 2rem !important; }
  
  .py-6 { padding-top: 3rem; padding-bottom: 3rem; }
  .section-title-saas { font-size: 1.75rem; }
  .mobile-badge-saas { font-size: 0.8rem; padding: 6px 12px; }
  .m-feature .fs-5 { font-size: 1rem !important; }
  .mobile-mockup-saas { max-height: 300px; }
  
  .benefit-item-saas { padding: 16px !important; flex-direction: column; text-align: center; }
  .b-icon-saas { margin: 0 auto 16px !important; }
  [dir="rtl"] .b-icon-saas { margin: 0 auto 16px !important; }
}
</style>
